export const AIRCRAFT_SUFFIX = "aircraft";

export interface AircraftFuel {
  kg: number;
  at: number;
  arrear?: number;
}

export function fuelRowValues(fuel: AircraftFuel): { value: string; json: string } {
  const json = fuel.arrear && fuel.arrear > 0 ? { at: fuel.at, arrear: fuel.arrear } : { at: fuel.at };
  return { value: String(fuel.kg), json: JSON.stringify(json) };
}

export function parseFuelRow(row: { value?: string; json?: string } | undefined | null): AircraftFuel | null {
  if (!row || !row.value || !row.json) return null;
  const kg = Number(row.value);
  if (!Number.isFinite(kg) || kg < 0) return null;
  let at: unknown;
  let arrear: unknown;
  try {
    const parsed = JSON.parse(row.json);
    at = parsed?.at;
    arrear = parsed?.arrear;
  } catch {
    return null;
  }
  if (typeof at !== "number" || !Number.isFinite(at) || at <= 0) return null;
  return typeof arrear === "number" && Number.isFinite(arrear) && arrear > 0 ? { kg, at, arrear } : { kg, at };
}

// The stored row is the last-write-wins winner; a device's own echo equals what it wrote.
export function shouldAdopt(known: AircraftFuel | null, stored: AircraftFuel | null): boolean {
  if (!stored) return false;
  return !known || known.kg !== stored.kg || known.at !== stored.at || (known.arrear ?? 0) !== (stored.arrear ?? 0);
}

export interface AircraftTimer {
  takeoffTime: number;
  landingTime: number;
  prevLanding: number;
  startPoint?: [number, number];
  startPlace?: string;
  startIcao?: string;
  takeoffFuel?: number;
  flightId: string;
  flightNumber: string;
}

export function timerRowValues(t: AircraftTimer): { value: string; json: string } {
  const { takeoffTime, ...rest } = t;
  return { value: String(takeoffTime), json: JSON.stringify(rest) };
}

const isNum = (v: unknown): v is number => typeof v === "number" && Number.isFinite(v);

export function parseTimerRow(row: { value?: string; json?: string } | undefined | null): AircraftTimer | null {
  if (!row) return null;
  const takeoffTime = Number(row.value);
  if (!row.value || !Number.isFinite(takeoffTime) || takeoffTime <= 0) return null;
  let j: any;
  try {
    j = JSON.parse(row.json || "{}");
  } catch {
    return null;
  }
  if (!j || typeof j !== "object") return null;
  const landingTime = j.landingTime === undefined ? 0 : j.landingTime;
  const prevLanding = j.prevLanding === undefined ? 0 : j.prevLanding;
  if (!isNum(landingTime) || landingTime < 0 || !isNum(prevLanding) || prevLanding < 0) return null;
  const t: AircraftTimer = {
    takeoffTime,
    landingTime,
    prevLanding,
    flightId: typeof j.flightId === "string" ? j.flightId : "",
    flightNumber: typeof j.flightNumber === "string" ? j.flightNumber : "",
  };
  if (Array.isArray(j.startPoint) && j.startPoint.length === 2 && j.startPoint.every(isNum)) {
    t.startPoint = [j.startPoint[0], j.startPoint[1]];
  }
  if (typeof j.startPlace === "string") t.startPlace = j.startPlace;
  if (typeof j.startIcao === "string") t.startIcao = j.startIcao;
  if (isNum(j.takeoffFuel)) t.takeoffFuel = j.takeoffFuel;
  return t;
}

export function timerRunning(t: AircraftTimer | null): boolean {
  return t != null && t.takeoffTime > 0 && t.landingTime === 0;
}

export function sameTimer(a: AircraftTimer | null, b: AircraftTimer | null): boolean {
  if (!a || !b) return !a && !b;
  // Through the parser: it gives one key order, whatever built the record.
  const norm = (t: AircraftTimer) => JSON.stringify(parseTimerRow(timerRowValues(t)));
  return norm(a) === norm(b);
}

export const TIMER_MAX_AGE_MS = 3 * 60 * 60 * 1000;

export type TimerAction = "none" | "adopt-start" | "adopt-leg" | "adopt-earlier" | "rewrite" | "stop";

export function resolveTimer(
  local: { takeoffTime: number; lastLanding: number },
  stored: AircraftTimer | null,
  now: number
): TimerAction {
  if (!stored) return "none";
  const localRunning = local.takeoffTime > 0;
  if (timerRunning(stored)) {
    if (!localRunning) {
      if (stored.takeoffTime <= local.lastLanding) return "none";
      if (now - stored.takeoffTime > TIMER_MAX_AGE_MS) return "none";
      return "adopt-start";
    }
    if (stored.takeoffTime === local.takeoffTime) return "none";
    if (stored.prevLanding > 0 && stored.prevLanding >= local.takeoffTime) return "adopt-leg";
    if (stored.takeoffTime < local.takeoffTime) return "adopt-earlier";
    return "rewrite";
  }
  if (localRunning) return stored.landingTime >= local.takeoffTime ? "stop" : "rewrite";
  return "none";
}

// The flight a device keeps when it adopts a stored record. "" means: take the flight of
// the record. A device keeps its own flight only when the record names none and continues
// the same flight: an earlier takeoff of the same leg, or a mid-air record, which starts
// at the end of the leg before it. A new leg after a landing starts later than that.
export function keptFlight(action: TimerAction, ownFlightId: string, stored: AircraftTimer): string {
  if (stored.flightId || !ownFlightId) return "";
  const sameFlight =
    action === "adopt-earlier" || (action === "adopt-leg" && stored.prevLanding === stored.takeoffTime);
  return sameFlight ? ownFlightId : "";
}

// A silent stop clears the last loaded flight when it is the flight of the stopped record or
// the flight this device's timer ran for. Else the next takeoff stamps a closed flight.
export function clearsLoadedFlight(loadedFlightId: string, stored: AircraftTimer, ownFlightId: string): boolean {
  return loadedFlightId !== "" && (loadedFlightId === stored.flightId || loadedFlightId === ownFlightId);
}

// The last landing an idle device knows after it read a stored record. The value is in
// memory only, so a restarted device gets it back from a stopped record.
export function landingAfterRecord(lastLanding: number, stored: AircraftTimer | null): number {
  return stored && stored.landingTime > lastLanding ? stored.landingTime : lastLanding;
}
